import React from 'react';
import './Skeleton.css';

interface TableSkeletonProps {
    /** Number of placeholder rows to render. */
    rows?: number;
    /** Number of columns per row (defaults to 5 when unknown). */
    columns?: number;
    /** Render a faint header strip above the rows (for full-area / initial loads). */
    showHeader?: boolean;
}

/**
 * Loading placeholder that mirrors the TableReport row layout (56px rows,
 * 16px padding, evenly-split columns) so the page keeps its shape while data
 * loads instead of blanking out behind a full-screen spinner.
 */
const SkeletonBar: React.FC<{ width: string; height?: number }> = ({ width, height = 12 }) => (
    <div className="finex-skeleton" style={{ width, height, borderRadius: 6 }} />
);

const COLUMN_WIDTHS = ['70%', '52%', '64%', '46%', '60%', '50%', '42%'];

const TableSkeleton: React.FC<TableSkeletonProps> = ({ rows = 8, columns = 5, showHeader = false }) => {
    const cols = Math.max(1, columns);
    return (
        <div style={{ width: '100%' }} aria-busy="true" aria-live="polite" role="status">
            {showHeader && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        padding: '10px 16px',
                        borderBottom: '1px solid #DBE4E4',
                    }}
                >
                    {Array.from({ length: cols }).map((_, c) => (
                        <div key={c} style={{ flex: 1 }}>
                            <SkeletonBar width="55%" height={10} />
                        </div>
                    ))}
                </div>
            )}
            {Array.from({ length: rows }).map((_, r) => (
                <div
                    key={r}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        width: '100%',
                        height: '56px',
                        padding: '10px 16px',
                        borderBottom: r === rows - 1 ? 'none' : '1px solid #DBE4E4',
                    }}
                >
                    {Array.from({ length: cols }).map((_, c) => (
                        <div key={c} style={{ flex: 1 }}>
                            <SkeletonBar width={COLUMN_WIDTHS[(r + c) % COLUMN_WIDTHS.length]} />
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

export default TableSkeleton;
